// HowToPlayModal.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled(motion.div)`
  background-color: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  padding: 20px;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  position: relative;
  box-sizing: border-box;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.text};
  font-size: 1.5rem;
  cursor: pointer;
  position: absolute;
  top: 16px;
  right: 16px;

  &:hover {
    color: ${({ theme }) => theme.accentColor};
  }
`;

const Title = styled.h2`
  margin-top: 0;
  text-align: center;
`;

const Content = styled.div`
  margin-top: 20px;
  font-size: .9rem;
  line-height: 1.5;
`;

const List = styled.ul`
  padding-left: 20px;
`;

interface HowToPlayModalProps {
    onClose: () => void;
}

const HowToPlayModal: React.FC<HowToPlayModalProps> = ({ onClose }) => {
    return (
        <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
        >
            <ModalContainer
                onClick={(e) => e.stopPropagation()} // Prevents closing the modal when clicking inside it
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
            >
                <CloseButton onClick={onClose} aria-label="Close">
                    &times;
                </CloseButton>
                <Title>How to Play</Title>
                <Content>
                    <p>
                        Welcome to Riddle Ravel! Your goal is to uncover a hidden category (e.g., “Rooms in Clue,” “Pandas,” “Super Bowl winners”) based on a series of clues
                        describing things in that category. Unlike
                        other daily games, each day’s puzzle is designed to stump most players. So, if you don’t guess
                        correctly, you’re likely in the majority.
                    </p>
                    <p>
                        <br></br>
                        Each clue describes one thing belonging to the hidden category. As you reveal more clues, you’ll
                        see additional things from that same category. Once you suspect you know the category, type it
                        into the guess box and submit.
                    </p>
                    <p>
                        <br></br>
                    </p>
                    <p>
                        1. Reveal Clues
                    <List>
                        <li>Click the <strong>First Hint</strong> button to show the initial clue.</li>
                        <li>Click the <strong>Next Hint</strong> button to reveal subsequent clues.</li>
                    </List>
                    </p>
                    <p>
                        2. One Guess per Day
                    </p>
                    <List>
                        <li>Use the revealed clues to figure out the category.</li>
                        <li>Type your final answer into the input field and click submit.</li>
                        <li>Your goal is to identify the category in as few guesses as possible.</li>
                    </List>
                    <p>
                        3. Track Your Progress
                    </p>
                    <List>
                        <li>Return each day for a new puzzle.</li>
                        <li>View your daily streaks and stats in the statistics section.</li>
                    </List>
                </Content>
            </ModalContainer>
        </Overlay>
    );
};

export default HowToPlayModal;
